import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Plus, Pencil, Trash2, Store, Eye } from 'lucide-react';

const VendorLedger = () => {
  const { API } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const [form, setForm] = useState({ name: '', mobile: '', email: '', linked_vehicles: [] });

  const fetchData = useCallback(async () => {
    try {
      const [vendorsRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/vendors`),
        axios.get(`${API}/vehicles`),
      ]);
      setVendors(vendorsRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      toast.error('Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => { setForm({ name: '', mobile: '', email: '', linked_vehicles: [] }); setEditingVendor(null); };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (vendor) => {
    setEditingVendor(vendor);
    setForm({ name: vendor.name, mobile: vendor.mobile, email: vendor.email || '', linked_vehicles: vendor.linked_vehicles || [] });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Enter vendor name'); return; }
    setSubmitting(true);
    try {
      if (editingVendor) {
        await axios.patch(`${API}/vendors/${editingVendor.id}`, form);
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API}/vendors`, form);
        toast.success('Vendor added');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this vendor?')) return;
    try {
      await axios.delete(`${API}/vendors/${id}`);
      toast.success('Vendor deactivated');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const viewLedger = async (vendor) => {
    setLedgerOpen(true);
    setLedgerLoading(true);
    setLedgerData(null);
    try {
      const res = await axios.get(`${API}/vendors/ledger/${vendor.id}`);
      setLedgerData(res.data);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLedgerLoading(false); }
  };

  const toggleVehicle = (vehicleId) => {
    setForm(prev => {
      const linked = prev.linked_vehicles.includes(vehicleId)
        ? prev.linked_vehicles.filter(v => v !== vehicleId)
        : [...prev.linked_vehicles, vehicleId];
      return { ...prev, linked_vehicles: linked };
    });
  };

  return (
    <div className="space-y-6" data-testid="vendor-ledger-section">
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900">Vendor Management</CardTitle>
            <Button onClick={openCreate} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="add-vendor-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Vendor
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          ) : vendors.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Store className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No vendors added yet</p>
            </div>
          ) : (
            <Table data-testid="vendors-table">
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor ID</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mobile</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Linked Vehicles</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.map((v) => (
                  <TableRow key={v.id} className="hover:bg-slate-50/50 border-b border-slate-100">
                    <TableCell className="font-mono text-xs">{v.vendor_id}</TableCell>
                    <TableCell className="font-medium text-sm">{v.name}</TableCell>
                    <TableCell className="text-sm">{v.mobile || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{v.email || '-'}</TableCell>
                    <TableCell className="text-xs">
                      {(v.linked_vehicles || []).map(vid => {
                        const veh = vehicles.find(x => x.id === vid);
                        return veh ? veh.vehicle_number : vid;
                      }).join(', ') || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => viewLedger(v)} data-testid={`view-ledger-${v.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(v)}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(v.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Vendor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) resetForm(); setDialogOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="vendor-dialog">
          <DialogHeader>
            <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
            <DialogDescription>Vendor details and linked vehicles</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Mobile</Label>
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="vendor-mobile" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vendor-email" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Linked Vehicles</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {vehicles.map((v) => (
                  <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 rounded">
                    <input type="checkbox" checked={form.linked_vehicles.includes(v.id)} onChange={() => toggleVehicle(v.id)} />
                    {v.vehicle_number} ({v.make} {v.model})
                  </label>
                ))}
              </div>
            </div>
            <Button className="w-full bg-slate-900 text-white hover:bg-slate-800" onClick={handleSubmit} disabled={submitting} data-testid="submit-vendor-btn">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {editingVendor ? 'Update' : 'Add'} Vendor
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vendor Ledger Dialog */}
      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="vendor-ledger-dialog">
          <DialogHeader>
            <DialogTitle>Vendor Ledger — {ledgerData?.vendor?.name}</DialogTitle>
            <DialogDescription>
              Total Paid: {(ledgerData?.total_paid || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })} |
              Total Received: {(ledgerData?.total_received || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })} |
              Balance: {(ledgerData?.balance || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
            </DialogDescription>
          </DialogHeader>
          {ledgerLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (ledgerData?.payments || []).length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">No transactions for this vendor</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Direction</TableHead>
                  <TableHead className="text-xs">Amount</TableHead>
                  <TableHead className="text-xs">Mode</TableHead>
                  <TableHead className="text-xs">Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(ledgerData?.payments || []).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-sm">{p.date}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded ${p.direction === 'received' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                        {p.direction}
                      </span>
                    </TableCell>
                    <TableCell className="font-semibold">{parseFloat(p.amount).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                    <TableCell className="text-xs">{p.mode}</TableCell>
                    <TableCell className="text-xs text-slate-500">{p.remarks || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorLedger;
